import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { marketAPI, aiAPI } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bot, TrendingUp, AlertTriangle, Target, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AIAssistantPage() {
  const [coins, setCoins] = useState([]);
  const [selectedCoinId, setSelectedCoinId] = useState('bitcoin');
  const [analysisType, setAnalysisType] = useState('comprehensive');
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    marketAPI.getTopCoins(20).then(r => setCoins(r.data)).catch(() => {});
    aiAPI.getHistory(10).then(r => setHistory(r.data)).catch(() => {});
  }, []);

  const selectedCoin = coins.find(c => c.id === selectedCoinId);

  const runAnalysis = async () => {
    if (!selectedCoin) { toast.error('Select a coin'); return; }
    setLoading(true);
    setAnalysis(null);
    try {
      const res = await aiAPI.analyze({
        coin_id: selectedCoin.id, coin_name: selectedCoin.name,
        current_price: selectedCoin.current_price, analysis_type: analysisType
      });
      setAnalysis(res.data);
      aiAPI.getHistory(10).then(r => setHistory(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed. Check AI service.');
    } finally { setLoading(false); }
  };

  const typeIcons = { trend: TrendingUp, signal: Target, risk: AlertTriangle, comprehensive: Bot };
  const typeColors = { trend: 'text-green-400', signal: 'text-emerald-400', risk: 'text-amber-400', comprehensive: 'text-green-400' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-assistant-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-green-500/15 flex items-center justify-center">
            <Bot className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">AI Trading Assistant</h1>
            <p className="text-xs text-[var(--text-secondary)]">Powered by Claude Sonnet 4.5</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Controls */}
          <div className="lg:col-span-4 space-y-4">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="ai-controls">
              <div>
                <label className="text-xs text-[var(--text-secondary)] mb-2 block">Select Cryptocurrency</label>
                <Select value={selectedCoinId} onValueChange={setSelectedCoinId}>
                  <SelectTrigger className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" data-testid="ai-coin-select">
                    <SelectValue placeholder="Select coin" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-elevated)] border-[var(--border-subtle)] max-h-60">
                    {coins.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-[var(--text-secondary)] text-sm">
                        <div className="flex items-center gap-2">
                          <img src={c.image} alt="" className="w-4 h-4 rounded-full" />
                          {c.name} ({c.symbol?.toUpperCase()})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCoin && (
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <div className="flex items-center gap-2 mb-2">
                    <img src={selectedCoin.image} alt="" className="w-6 h-6 rounded-full" />
                    <span className="text-sm font-semibold text-[var(--text-primary)]">{selectedCoin.name}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-[var(--text-secondary)]">Price</span>
                      <p className="font-mono text-[var(--text-primary)]">${selectedCoin.current_price?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)]">24h Change</span>
                      <p className={`font-mono ${(selectedCoin.price_change_24h || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(selectedCoin.price_change_24h || 0) >= 0 ? '+' : ''}{(selectedCoin.price_change_24h || 0).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-[var(--text-secondary)] mb-2 block">Analysis Type</label>
                <Tabs value={analysisType} onValueChange={setAnalysisType} className="w-full">
                  <TabsList className="w-full bg-[var(--bg-primary)] border border-[var(--border-subtle)] h-auto flex-wrap gap-1 p-1">
                    <TabsTrigger value="comprehensive" className="flex-1 data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400 text-xs py-2">Full</TabsTrigger>
                    <TabsTrigger value="trend" className="flex-1 data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400 text-xs py-2">Trend</TabsTrigger>
                    <TabsTrigger value="signal" className="flex-1 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-xs py-2">Signal</TabsTrigger>
                    <TabsTrigger value="risk" className="flex-1 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs py-2">Risk</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <button
                onClick={runAnalysis} disabled={loading || !selectedCoin}
                data-testid="run-analysis-btn"
                className="w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</> : <><Bot className="w-4 h-4" /> Run Analysis</>}
              </button>
            </div>

            {/* History */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="ai-history">
              <div className="px-5 py-3 border-b border-[var(--border-subtle)]">
                <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)]">Recent Analyses</h3>
              </div>
              <div className="max-h-[300px] overflow-y-auto divide-y divide-[var(--divide-color)]">
                {history.length === 0 ? (
                  <p className="p-4 text-xs text-[var(--text-secondary)] text-center">No analyses yet</p>
                ) : history.map((h, i) => {
                  const Icon = typeIcons[h.analysis_type] || Bot;
                  const color = typeColors[h.analysis_type] || 'text-green-400';
                  return (
                    <button key={i} onClick={() => setAnalysis({ analysis: h.response, coin: h.coin_name, type: h.analysis_type })} className="w-full px-5 py-3 text-left hover:bg-[var(--hover-bg)] transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-3.5 h-3.5 ${color}`} />
                        <span className="text-xs font-medium text-[var(--text-primary)]">{h.coin_name}</span>
                        <span className="text-[10px] text-[var(--text-tertiary)]">{h.analysis_type}</span>
                      </div>
                      <p className="text-[10px] text-[var(--text-secondary)] line-clamp-1">{h.response?.substring(0, 80)}...</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Analysis Result */}
          <div className="lg:col-span-8">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded min-h-[500px]" data-testid="ai-result">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-[500px] gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-2 border-green-500/30 flex items-center justify-center">
                      <Bot className="w-8 h-8 text-green-400 animate-pulse" />
                    </div>
                    <div className="absolute inset-0 rounded-full border-2 border-green-500 border-t-transparent animate-spin" />
                  </div>
                  <p className="text-sm text-[var(--text-secondary)]">AI is analyzing {selectedCoin?.name}...</p>
                </div>
              ) : analysis ? (
                <div>
                  <div className="px-6 py-4 border-b border-[var(--border-subtle)] flex items-center gap-3">
                    {(() => { const Icon = typeIcons[analysis.type] || Bot; const color = typeColors[analysis.type] || 'text-green-400'; return <Icon className={`w-5 h-5 ${color}`} />; })()}
                    <div>
                      <h2 className="text-sm font-semibold text-[var(--text-primary)]">{analysis.coin} Analysis</h2>
                      <p className="text-xs text-[var(--text-secondary)] capitalize">{analysis.type} analysis</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="prose prose-invert prose-sm max-w-none [&>p]:text-[var(--text-secondary)] [&>ul]:text-[var(--text-secondary)] [&>ol]:text-[var(--text-secondary)] [&>h1]:text-[var(--text-primary)] [&>h2]:text-[var(--text-primary)] [&>h3]:text-[var(--text-primary)] [&>strong]:text-[var(--text-primary)]">
                      {analysis.analysis?.split('\n').map((line, i) => {
                        if (!line.trim()) return <br key={i} />;
                        if (line.startsWith('**') && line.endsWith('**')) return <h3 key={i} className="text-base font-semibold font-['Outfit'] text-[var(--text-primary)] mt-4 mb-2">{line.replace(/\*\*/g, '')}</h3>;
                        if (line.startsWith('# ')) return <h2 key={i} className="text-lg font-bold font-['Outfit'] text-[var(--text-primary)] mt-4 mb-2">{line.slice(2)}</h2>;
                        if (line.startsWith('## ')) return <h3 key={i} className="text-base font-semibold font-['Outfit'] text-[var(--text-primary)] mt-3 mb-2">{line.slice(3)}</h3>;
                        if (line.startsWith('- ') || line.startsWith('* ')) return <div key={i} className="flex gap-2 py-0.5 text-sm text-[var(--text-secondary)]"><span className="text-green-400 mt-1 shrink-0">-</span><span>{line.slice(2)}</span></div>;
                        if (line.match(/^\d+\./)) return <div key={i} className="flex gap-2 py-0.5 text-sm text-[var(--text-secondary)]"><span className="text-green-400 font-mono shrink-0">{line.match(/^\d+/)[0]}.</span><span>{line.replace(/^\d+\.\s*/, '')}</span></div>;
                        return <p key={i} className="text-sm text-[var(--text-secondary)] py-0.5">{line}</p>;
                      })}
                    </div>
                    <div className="mt-6 p-3 bg-amber-500/10 border border-amber-500/20 rounded">
                      <p className="text-xs text-amber-400/80">Disclaimer: AI-generated analysis is for informational purposes only. Always do your own research before making trading decisions.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[500px] gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Bot className="w-8 h-8 text-green-400/50" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-[var(--text-secondary)]">Select a coin and run analysis</p>
                    <p className="text-xs text-[var(--text-tertiary)] mt-1">Get AI-powered market insights, trading signals, and risk assessment</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
